import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Minus, Plus, ShoppingBag, Trash2, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { fcfa, ttc } from "@/lib/format";

const CartDrawer = () => {
  const { open, setOpen, items, setQty, remove, total } = useCart();
  const navigate = useNavigate();
  const franceFees = items.reduce((s, p) => s + (p.shipping_fee || 0) * p.quantity, 0);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            data-testid="cart-drawer-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-50 bg-bark/50 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col border-l border-clay bg-sand"
          >
            <div className="flex items-center justify-between border-b border-clay px-6 py-5">
              <h2 className="font-display text-2xl font-semibold text-bark">Votre panier</h2>
              <button
                data-testid="cart-drawer-close"
                onClick={() => setOpen(false)}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-clay text-bark transition-colors duration-300 hover:border-terracotta hover:text-terracotta"
                aria-label="Fermer le panier"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
                  <ShoppingBag className="h-12 w-12 text-clay" />
                  <p className="font-display text-xl text-bark/60">Votre panier est vide</p>
                  <button
                    data-testid="cart-empty-browse-button"
                    onClick={() => { setOpen(false); navigate("/boutique"); }}
                    className="text-sm font-semibold uppercase tracking-[0.2em] text-terracotta underline-offset-4 hover:underline"
                  >
                    Découvrir la boutique
                  </button>
                </div>
              ) : (
                <ul className="divide-y divide-clay">
                  {items.map((item) => (
                    <li key={item.id} data-testid={`cart-item-${item.id}`} className="flex gap-4 py-5">
                      <div className="h-24 w-20 shrink-0 overflow-hidden border border-clay bg-cream">
                        <img src={item.image} alt={item.name} className="h-full w-full object-cover" />
                      </div>
                      <div className="flex flex-1 flex-col justify-between">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-bark/40">{item.category}</p>
                            <p className="font-display text-lg leading-tight text-bark">{item.name}</p>
                          </div>
                          <button
                            data-testid={`cart-remove-${item.id}`}
                            onClick={() => remove(item.id)}
                            className="text-bark/30 transition-colors duration-300 hover:text-terracotta"
                            aria-label="Retirer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center border border-clay">
                            <button data-testid={`cart-qty-minus-${item.id}`} onClick={() => setQty(item.id, item.quantity - 1)} className="flex h-8 w-8 items-center justify-center text-bark/60 hover:text-terracotta">
                              <Minus className="h-3.5 w-3.5" />
                            </button>
                            <span data-testid={`cart-qty-${item.id}`} className="w-8 text-center text-sm font-semibold">{item.quantity}</span>
                            <button data-testid={`cart-qty-plus-${item.id}`} onClick={() => setQty(item.id, item.quantity + 1)} className="flex h-8 w-8 items-center justify-center text-bark/60 hover:text-terracotta">
                              <Plus className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          <p className="text-sm font-bold text-terracotta">{fcfa(ttc(item) * item.quantity)}</p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-clay px-6 py-6">
                <div className="mb-2 flex items-center justify-between">
                  <span className="font-display text-lg font-semibold text-bark">Total TTC</span>
                  <span data-testid="cart-total" className="font-display text-2xl font-bold text-terracotta">{fcfa(total + franceFees)}</span>
                </div>
                <p data-testid="cart-ttc-note" className="mb-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-bark/45">Tous frais inclus</p>
                <p className="mb-5 text-xs text-bark/40">Livraison à domicile : Dakar, Thiès, Mbour & Saint-Louis — tarif exact selon votre ville à l'étape suivante.</p>
                <button
                  data-testid="cart-checkout-button"
                  onClick={() => { setOpen(false); navigate("/commander"); }}
                  className="group flex h-14 w-full items-center justify-center gap-3 rounded-full bg-terracotta text-sm font-bold uppercase tracking-[0.2em] text-sand transition-colors duration-300 hover:bg-terracotta-dark"
                >
                  Passer la commande
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
